import type { ReactNode } from 'react';

/**
 * OWNER-OPERATING-SYSTEM-V1 selector bar (THE HOME RULING, growth brief
 * s.19.1; shell composition s.20.7). The controlled-customization control
 * row for Home/Reports: date range / scope / department / comparison /
 * saved view -- APPROVED FILTERS ONLY, no arbitrary widget layouts.
 *
 * Every control renders as a native <select> styled as a chip: keyboard
 * reachable, no-JS complete, no overlay machinery. Options arrive from the
 * caller (org-config-backed vocabularies -- nothing here mints a filter).
 * A control with exactly one option renders as a static chip (an honest
 * single-value lens, e.g. departments before the taxonomy is seeded).
 */

export interface SelectorOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectorControlDef {
  readonly key: string;
  /** Accessible name for the control (not rendered as a visible label). */
  readonly label: string;
  readonly options: ReadonlyArray<SelectorOption>;
  readonly value: string;
  readonly onChange?: (value: string) => void;
  /** 'saved' renders the saved-view chip treatment (HOME RULING). */
  readonly kind?: 'filter' | 'saved';
}

export interface SelectorBarProps {
  readonly controls: ReadonlyArray<SelectorControlDef>;
  readonly children?: ReactNode;
  readonly testId?: string;
}

export default function SelectorBar({
  controls,
  children,
  testId,
}: SelectorBarProps): JSX.Element {
  return (
    <div className="ac-selbar" data-testid={testId}>
      {controls.map((control) => {
        const chipClass =
          control.kind === 'saved'
            ? 'ac-selbar__chip ac-selbar__chip--saved'
            : 'ac-selbar__chip';
        if (control.options.length <= 1) {
          const only = control.options[0];
          return (
            <span key={control.key} className={chipClass} title={control.label}>
              {only ? only.label : control.label}
            </span>
          );
        }
        return (
          <label key={control.key} className={chipClass}>
            <span className="ac-sr-only">{control.label}</span>
            <select
              className="ac-selbar__select"
              value={control.value}
              onChange={(e) => control.onChange?.(e.target.value)}
              aria-label={control.label}
            >
              {control.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        );
      })}
      {children}
    </div>
  );
}
